/**
 * 화면에서 지금 돌고 있는 작업 목록 — 채팅 AI 요청에 `work_state` 로 동봉한다.
 *
 * 개요·이미지형 PPT·학습지 그림·한글 내보내기 등은 답변 스트림 밖에서 화면이 돌려 모델 기록에 없다.
 * 알려 주지 않으면 AI 가 아무것도 안 도는데 "지금 만들고 있어요"라고 하거나, 도는 작업을 또 시작한다.
 *   { jobs: [{ kind, elapsed_s, done?, total?, count? }] } — 빈 목록은 "돌고 있는 작업 없음"
 * kind·보통 걸리는 시간은 백엔드 services/chat_work_state.WORK_KINDS 와 같은 계약.
 */

export const WORK_KINDS = [
  'image',
  'ppt_outline',
  'worksheet_outline',
  'text_deck',
  'image_deck',
  'worksheet_image',
  'page_edit',
  'page_redraw',
  'ppt_export',
  'hwpx_export',
  'upload',
];

const KNOWN = new Set(WORK_KINDS);

const toCount = (value) => (Number.isFinite(value) && value > 0 ? Math.floor(value) : 0);

/**
 * 켜진 작업의 시작 시각과 대화를 기록하고 꺼진 작업은 지운다 (store 를 직접 고친다).
 * flags: { [kind]: boolean } — 이미 기록된 작업은 시작 시각을 유지한다.
 */
export const trackWorkStarts = (store, flags, conversationId = null, now = Date.now()) => {
  Object.entries(flags || {}).forEach(([kind, active]) => {
    if (!KNOWN.has(kind)) return;
    if (!active) {
      delete store[kind];
    } else if (!store[kind]) {
      store[kind] = { startedAt: now, conversationId: conversationId || null };
    } else if (!store[kind].conversationId && conversationId) {
      store[kind] = { ...store[kind], conversationId };
    }
  });
  return store;
};

/**
 * jobs: [{ kind, startedAt, conversationId?, done?, total?, count? }] — 지금 켜진 작업만.
 * 다른 대화에서 시작한 작업은 뺀다. 작업이 없으면 includeEmpty 일 때만 빈 목록을 보낸다.
 */
export const buildChatWorkState = ({ jobs = [], conversationId = null, includeEmpty = false, now = Date.now() } = {}) => {
  const seen = new Set();
  const list = [];
  (Array.isArray(jobs) ? jobs : []).forEach((job) => {
    if (!job || !KNOWN.has(job.kind) || seen.has(job.kind)) return;
    if (job.conversationId && conversationId && job.conversationId !== conversationId) return;
    seen.add(job.kind);
    const startedAt = Number.isFinite(job.startedAt) ? job.startedAt : now;
    const item = { kind: job.kind, elapsed_s: Math.max(0, Math.round((now - startedAt) / 1000)) };
    const total = toCount(job.total);
    if (total) {
      item.total = total;
      item.done = Math.min(toCount(job.done), total);
    }
    const count = toCount(job.count);
    if (count) item.count = count;
    list.push(item);
  });
  if (!list.length && !includeEmpty) return null;
  return { jobs: list };
};

const pending = new Map();

/** 보낼 요청의 화면 대화 ID 로 목록을 맡겨 둔다. chatApi 가 같은 ID 로 한 번 꺼내 쓴다. */
export const stashChatWorkState = (conversationId, state) => {
  if (!conversationId) return;
  if (state) pending.set(conversationId, state);
  else pending.delete(conversationId);
};

export const takeChatWorkState = (conversationId) => {
  if (!conversationId || !pending.has(conversationId)) return null;
  const state = pending.get(conversationId);
  pending.delete(conversationId);
  return state;
};
